import type { InvoiceMailLogEntry } from '@embertime/shared'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useRef, useState } from 'react'
import type { InvoiceDetail } from '../../lib/api'
import { api } from '../../lib/api'
import { previousDispatch, sendErrorText } from './invoice-mail-logic'
import { InvoiceMailOverview } from './invoice-mail-overview'
import { InvoiceMailSheet } from './invoice-mail-sheet'

// #536 container: loads state and log, opens the send sheet, and asks the
// server for the journal proof. The proof is checked once automatically for a
// fresh dispatch (the journal needs a few seconds) and on demand afterwards.

export function InvoiceMailPanel({ invoice }: { invoice: InvoiceDetail }) {
  const qc = useQueryClient()
  const [sheetOpen, setSheetOpen] = useState(false)
  const [checking, setChecking] = useState<string | null>(null)
  const [archiveError, setArchiveError] = useState<string | null>(null)
  const trigger = useRef<HTMLDivElement>(null)
  const autoChecked = useRef(new Set<string>())

  const stateQuery = useQuery({
    queryKey: ['invoice', invoice.id, 'mail-state'],
    queryFn: () => api.getInvoiceMailState(invoice.id),
  })
  const logQuery = useQuery({
    queryKey: ['invoice', invoice.id, 'mail-log'],
    queryFn: () => api.getInvoiceMailLog(invoice.id),
  })
  const entries = logQuery.data?.entries ?? []

  const storeEntry = useCallback(
    (entry: InvoiceMailLogEntry) => {
      qc.setQueryData<{ entries: InvoiceMailLogEntry[] }>(
        ['invoice', invoice.id, 'mail-log'],
        (current) => {
          const rest = (current?.entries ?? []).filter((e) => e.attemptId !== entry.attemptId)
          return {
            entries: [entry, ...rest].sort((a, b) => b.reservedAt.localeCompare(a.reservedAt)),
          }
        },
      )
    },
    [qc, invoice.id],
  )

  const checkArchive = useCallback(
    async (attemptId: string) => {
      setChecking(attemptId)
      setArchiveError(null)
      try {
        const { entry } = await api.checkInvoiceMailArchive(invoice.id, attemptId)
        storeEntry(entry)
      } catch (err) {
        const e = err as Error & { code?: string }
        setArchiveError(
          e.code === 'INVOICE_MAIL_ARCHIVE_UNREACHABLE'
            ? 'Journal-Archiv gerade nicht erreichbar. Nachweis bleibt ausstehend.'
            : sendErrorText(e.code, e.message),
        )
      } finally {
        setChecking(null)
      }
    },
    [invoice.id, storeEntry],
  )

  // One automatic check per fresh, unproven dispatch (younger than a day).
  useEffect(() => {
    const fresh = entries.find(
      (entry) =>
        entry.status === 'sent' &&
        entry.archiveStatus !== 'verified' &&
        Date.now() - Date.parse(entry.acceptedAt ?? entry.reservedAt) < 24 * 3600 * 1000 &&
        !autoChecked.current.has(entry.attemptId),
    )
    if (!fresh) return
    autoChecked.current.add(fresh.attemptId)
    const timer = window.setTimeout(() => void checkArchive(fresh.attemptId), 5000)
    return () => window.clearTimeout(timer)
  }, [entries, checkArchive])

  const closeSheet = useCallback(() => {
    setSheetOpen(false)
    // Return focus to the dispatch card that opened the sheet.
    window.setTimeout(
      () => trigger.current?.querySelector<HTMLButtonElement>('#invoice-mail-open')?.focus(),
      0,
    )
  }, [])

  if (!stateQuery.data || !logQuery.data)
    return (
      <div className="rounded-2xl border border-border-default bg-bg-elevated/50 p-5 text-sm text-text-secondary">
        {stateQuery.isError || logQuery.isError
          ? 'Versandstatus konnte nicht geladen werden.'
          : 'Versandstatus wird geprüft …'}
      </div>
    )

  return (
    <div ref={trigger}>
      <InvoiceMailOverview
        invoice={invoice}
        state={stateQuery.data}
        entries={entries}
        onOpenSheet={() => setSheetOpen(true)}
        onArchiveCheck={(attemptId) => void checkArchive(attemptId)}
        checking={checking}
        archiveError={archiveError}
      />
      {stateQuery.data.enabled && (
        <InvoiceMailSheet
          invoice={invoice}
          state={stateQuery.data}
          open={sheetOpen}
          previous={previousDispatch(entries)}
          onClose={closeSheet}
          onSent={(entry) => {
            storeEntry(entry)
            setSheetOpen(false)
            qc.invalidateQueries({ queryKey: ['invoice', invoice.id, 'mail-log'] })
            window.scrollTo({ top: 0, behavior: 'smooth' })
          }}
        />
      )}
    </div>
  )
}
